/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";
import ReactSelect, { components as selectComponents } from "react-select";
import CreatableSelect from "react-select/creatable";
import AsyncCreatableSelect from "react-select/async-creatable";
import AsyncSelect from "react-select/async";
import get from "lodash/get";

import { gatherClasses, isFunction, isObject } from "../../lib/utils";
import SelectUtils from "../../lib/selectUtils";
import InputFeedback from "../inputFeedback/inputFeedback";
import {
  DefaultLoaderComponent,
  DefaultOptionComponent,
  HiddenDropdownIndicator,
  HiddenDropdownSeparator,
  SelectDeselectMenuList,
  OnlySelectAllMenuList
} from "./customComponents";
import "./select.scss";

class OrionSelectComponent extends React.Component {
  constructor(props) {
    super(props);
    this.selectUtils = new SelectUtils();
    this.selectRef = props.innerRef || React.createRef();
  }

  addUserProvidedComponents = defaultComponents => {
    const { customComponents } = this.props;

    if (isFunction(customComponents)) {
      const userComponents = customComponents(selectComponents);
      const userComponentsKeys = Object.keys(userComponents);

      if (userComponentsKeys.length > 0) {
        userComponentsKeys.forEach(componentKey => {
          defaultComponents[componentKey] = userComponents[componentKey];
        });
      }
    }
  };

  getCustomComponents = () => {
    const {
      hideDownArrow,
      allowSelectDeselectAll,
      isMulti,
      selectAllOption,
      footerRenderer
    } = this.props;
    const defaultComponents = {};

    defaultComponents.LoadingIndicator = DefaultLoaderComponent;
    defaultComponents.Option = DefaultOptionComponent;

    this.addUserProvidedComponents(defaultComponents);

    if (hideDownArrow) {
      defaultComponents.DropdownIndicator = HiddenDropdownIndicator;
      defaultComponents.IndicatorSeparator = HiddenDropdownSeparator;
    }

    if (allowSelectDeselectAll === true && isMulti === true) {
      defaultComponents.MenuList = SelectDeselectMenuList;
    }

    if (selectAllOption || footerRenderer) {
      defaultComponents.MenuList = OnlySelectAllMenuList;
    }

    return defaultComponents;
  };

  render() {
    const {
      type,
      isInvalid,
      disableInputFeedback,
      groupLabelRenderer,
      innerRef,
      ...attr
    } = this.props;

    const Tag = {
      async: AsyncSelect,
      "async-creatable": AsyncCreatableSelect,
      creatable: CreatableSelect,
      select: ReactSelect
    }[type];

    if (!Tag) {
      throw new Error(`Unsupported Select type: ${type}`);
    }

    return (
      // eslint-disable-next-line react/jsx-fragments
      <React.Fragment>
        <Tag
          aria-label={attr.name || "select dropdown"}
          className={gatherClasses(
            this.props,
            "oui-select",
            isInvalid ? "oui-is-invalid" : null
          )}
          classNamePrefix="oui-select"
          menuIsOpen={attr.isMenuOpenAtStart || undefined}
          {...this.selectUtils.getFilterOption(attr, type)}
          {...this.selectUtils.getMenuPortalTarget(attr)}
          {...this.selectUtils.getPropsForType(attr, type)}
          {...this.selectUtils.getNoOptionsMessage(attr, type)}
          styles={this.selectUtils.getSelectStyles(attr.styles)}
          onChange={(values, meta) => {
            let changedValue = values;

            if (
              isObject(attr.selectAllOption) &&
              attr.selectAllOption.label &&
              attr.selectAllOption.value &&
              attr.isMulti === true
            ) {
              if (
                values.length > 0 &&
                values[0].value === attr.selectAllOption.value
              ) {
                changedValue = values.slice(1);
              }
            }

            if (
              attr.validationProps &&
              attr.validationProps.setFieldValue &&
              attr.name
            ) {
              attr.validationProps.setFieldValue(attr.name, changedValue, true);
            }

            return attr.onChange(changedValue, meta);
          }}
          onBlur={() => {
            if (
              attr.validationProps &&
              attr.validationProps.setFieldTouched &&
              attr.name
            ) {
              attr.validationProps.setFieldTouched(attr.name);
            }
          }}
          components={this.getCustomComponents()}
          ref={this.selectRef}
          internalRef={this.selectRef}
          closeMenuOnSelect={!attr.isMulti}
          hideSelectedOptions={attr.isMulti}
          formatGroupLabel={data => {
            return (
              <div
                className={gatherClasses(
                  null,
                  "oui-select__group-heading-inner",
                  !groupLabelRenderer && !data.label ? "oui-pb-0 oui-pt-2" : ""
                )}
              >
                {isFunction(groupLabelRenderer)
                  ? groupLabelRenderer(data)
                  : data.label}
              </div>
            );
          }}
          userProps={{
            onChange: attr.onChange,
            validationProps: attr.validationProps,
            name: attr.name,
            selectAllButtonText: attr.selectAllButtonText,
            selectAllOption: attr.selectAllOption,
            selectAllOptionDisabled: attr.selectAllOptionDisabled,
            selectAllOptionDisabledTooltip: attr.selectAllOptionDisabledTooltip,
            footerRenderer: attr.footerRenderer,
            isMulti: attr.isMulti
          }}
        />
        {!disableInputFeedback && attr.validationProps && (
          <InputFeedback
            show={isInvalid}
            message={get(attr.validationProps.errors, attr.name)}
          />
        )}
      </React.Fragment>
    );
  }
}

const OrionSelect = React.forwardRef((props, ref) => (
  <OrionSelectComponent {...props} innerRef={ref} />
));

OrionSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ).isRequired,
  type: PropTypes.oneOf(["async", "async-creatable", "creatable", "select"]),
  isInvalid: PropTypes.bool,
  autoFocus: PropTypes.bool,
  filterConfig: PropTypes.shape({
    ignoreCase: PropTypes.bool,
    ignoreAccents: PropTypes.bool,
    trim: PropTypes.bool,
    matchFrom: PropTypes.oneOf(["start", "any"])
  }),
  inputValue: PropTypes.string,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  isSearchable: PropTypes.bool,
  noOptionsMessage: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onCreateOption: PropTypes.func,
  onInputChange: PropTypes.func,
  openMenuOnClick: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  defaultOptions: PropTypes.any,
  loadOptions: PropTypes.func,
  cacheOptions: PropTypes.bool,
  customComponent: PropTypes.func,
  disableInputFeedback: PropTypes.bool,
  menuPlacement: PropTypes.oneOf(["auto", "bottom", "top"]),
  menuPosition: PropTypes.oneOf(["absolute", "fixed"]),
  maxMenuHeight: PropTypes.number,
  debounceDelay: PropTypes.number,
  isLoading: PropTypes.bool,
  innerRef: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.func
  ]),
  selectAllOptionDisabled: PropTypes.bool,
  selectAllOptionDisabledTooltip: PropTypes.string,
  isOptionDisabled: PropTypes.func
};

OrionSelect.defaultProps = {
  type: "select",
  options: [],
  isInvalid: false,
  autoFocus: false,
  isDisabled: false,
  isMulti: false,
  isSearchable: true,
  placeholder: "Select",
  cacheOptions: false,
  openMenuOnClick: true,
  disableInputFeedback: false,
  onChange: () => {},
  onBlur: () => {},
  menuPlacement: "auto",
  menuPosition: "absolute",
  maxMenuHeight: 200,
  isMenuOpenAtStart: false,
  debounceDelay: 0
};

OrionSelect.displayName = "OrionSelect";

export default OrionSelect;
